import { FC, SyntheticEvent, useState } from 'react';
import BootstrapForm from 'react-bootstrap/Form';

import ELEMENT, { createTestId } from 'common/element';

import Dialog from 'components/common/Dialog/Dialog';

import './PromptDialog.scss';

type PromptDialogProps = {
  title: string;
  label: string;
  value: string;
  inline?: boolean;
  show: boolean;
  close: () => void;
  onChange: (event: SyntheticEvent) => void;
  onSubmit: () => void;
  getError?: (value: string) => string | null;
  testId?: ELEMENT;
};

/**
 * This component is a simple specialized dialog that asks the user for a single string, for example the name
 * of a dashboard. The dialog will show a label and then a text input on the same line.
 *
 * @prop title       Title text for the dialog
 * @prop label       Label for the text input
 * @prop value       Initial value of the text input
 * @prop inline      Flag to show form inline
 * @prop show        Flag indicating that the dialog is open
 * @prop close       Calling this function should set the show flag to false.
 * @prop onChange    Function to call when the value of the text input changes
 * @prop onSubmit    Function to call when the form has been submitted (OK button or Enter key)
 * @prop getError    Optional function that returne error text (or null if no error)
 * @prop testId      uniquely identifying string for testing
 */
const PromptDialog: FC<PromptDialogProps> = ({
  title,
  label,
  value = '',
  inline = true,
  show,
  close,
  onChange,
  onSubmit,
  getError,
  testId,
}: PromptDialogProps) => {
  const inputTestId = testId ? createTestId(testId, 'input') : undefined;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const checkValue = (ev: SyntheticEvent): void => {
    if (getError) {
      const eventTarget = ev.target as HTMLInputElement;
      setErrorMessage(getError(eventTarget.value));
    }
    onChange(ev);
  };

  // Must cleanup local state before closing
  const cleanupAndClose = (): void => {
    setErrorMessage(null);
    close();
  };

  return (
    <Dialog
      title={title}
      show={show}
      close={cleanupAndClose}
      okCallback={onSubmit}
      okButtonDisabled={errorMessage != null}
      onEnterCallback={onSubmit}
      testId={testId}
    >
      <BootstrapForm inline={inline}>
        <BootstrapForm.Label className="PromptDialog__Label">{label}</BootstrapForm.Label>
        {errorMessage && <div className="PromptDialog__Error">{errorMessage}</div>}
        <BootstrapForm.Control
          as="input"
          value={value}
          onChange={checkValue}
          data-test-id={inputTestId}
          className="PromptDialog__Input"
        />
      </BootstrapForm>
    </Dialog>
  );
};

export default PromptDialog;
